/**
 * Which OLDER budget versions the Budget Overview shows as columns — the
 * `Compare: N of M ▾` picker's state (owner, 2026-10-05: *"we have lots of older
 * budgets for compare. I think we need a way to select which older budgets we
 * want to see."*). The table and the Cost Report PDF both render exactly this
 * selection; the Variance Analysis PDF does NOT read it (it keeps its baseline).
 *
 * THE RULES (each pinned by `budgetCompareSelection.test.js`) → `components/budget/CLAUDE.md` (Manage Budgets), moved there 2026-10-28.
 */
import { useCallback, useMemo, useState } from 'react';
import { MAX_COMPARE_VERSIONS } from '../../utils/budgetReportPdf';

export const COMPARE_STORAGE_KEY = 'sfx_budget_overview_compare';

const num = (v) => Number(v);

/** Non-current versions, newest first. */
export function olderVersionsOf(versions) {
  return (versions || [])
    .filter((v) => v && !v.is_current)
    .slice()
    .sort((a, b) => (num(b.version_number) - num(a.version_number)) || (num(b.id) - num(a.id)));
}

/** Keep `ids` that exist in `older`, in `older`'s order, at most `max`. */
function inOrderCapped(older, ids, max) {
  const want = new Set(ids.map(num));
  return older.filter((v) => want.has(num(v.id))).slice(0, max).map((v) => num(v.id));
}

export function defaultCompareIds(older, max = MAX_COMPARE_VERSIONS) {
  if (older.length === 0) return [];
  const ids = [older[0].id];
  const baseline = older.find((v) => v.is_baseline);
  if (baseline) ids.push(baseline.id);
  return inOrderCapped(older, ids, max);
}

export const latestOnlyIds = (older) => (older.length ? [num(older[0].id)] : []);
export const showAllIds = (older, max = MAX_COMPARE_VERSIONS) =>
  older.slice(0, max).map((v) => num(v.id));

/** A stored entry is `{ ids: number[], seen: number }`; anything else → null. */
function validEntry(entry) {
  if (!entry || typeof entry !== 'object' || !Array.isArray(entry.ids)) return null;
  const ids = entry.ids.map(num).filter((n) => Number.isFinite(n));
  const seen = Number.isFinite(num(entry.seen)) ? num(entry.seen) : null;
  return { ids, seen };
}

export function resolveCompareIds(older, stored, max = MAX_COMPARE_VERSIONS) {
  const entry = validEntry(stored);
  if (!entry) return defaultCompareIds(older, max);
  const known = new Set(older.map((v) => num(v.id)));
  const kept = entry.ids.filter((id) => known.has(id));
  const fresh = entry.seen == null ? [] : older.filter((v) => num(v.id) > entry.seen).map((v) => num(v.id));
  if (kept.length === 0 && fresh.length === 0 && entry.ids.length > 0) {
    return defaultCompareIds(older, max);
  }
  return inOrderCapped(older, [...kept, ...fresh], max);
}

function readMap() {
  try {
    const raw = localStorage.getItem(COMPARE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function readStoredCompare(departmentId) {
  if (departmentId == null) return null;
  return readMap()[String(departmentId)] ?? null;
}

export function writeStoredCompare(departmentId, ids, older) {
  if (departmentId == null) return;
  const seen = older.reduce((m, v) => Math.max(m, num(v.id)), 0);
  try {
    const map = readMap();
    map[String(departmentId)] = { ids: ids.map(num), seen };
    localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(map));
  } catch { /* storage unavailable — the choice lasts this session only */ }
}

/**
 * `useBudgetCompareSelection(departmentId, versions)` →
 * `{ older, selectedIds, max, atCap, toggle(id), latestOnly(), showAll() }`.
 * `selectedIds` is in table order (newest first).
 */
export function useBudgetCompareSelection(departmentId, versions, max = MAX_COMPARE_VERSIONS) {
  const older = useMemo(() => olderVersionsOf(versions), [versions]);
  // Session copy of the user's choice per department; `undefined` = not chosen
  // in this session, so read storage (read-only).
  const [chosen, setChosen] = useState({});
  const key = String(departmentId);
  const stored = Object.prototype.hasOwnProperty.call(chosen, key)
    ? chosen[key]
    : readStoredCompare(departmentId);
  // A storage read is a fresh object every render, so the memo keys on its TEXT.
  const storedText = JSON.stringify(stored ?? null);
  const selectedIds = useMemo(
    () => resolveCompareIds(older, JSON.parse(storedText), max),
    [older, storedText, max],
  );

  const commit = useCallback((ids) => {
    const next = inOrderCapped(older, ids, max);
    const seen = older.reduce((m, v) => Math.max(m, num(v.id)), 0);
    setChosen((c) => ({ ...c, [key]: { ids: next, seen } }));
    writeStoredCompare(departmentId, next, older);
  }, [older, max, key, departmentId]);

  const toggle = useCallback((id) => {
    const n = num(id);
    if (selectedIds.includes(n)) commit(selectedIds.filter((x) => x !== n));
    else if (selectedIds.length < max) commit([...selectedIds, n]);
  }, [selectedIds, max, commit]);

  return {
    older,
    selectedIds,
    max,
    atCap: selectedIds.length >= max,
    toggle,
    latestOnly: useCallback(() => commit(latestOnlyIds(older)), [commit, older]),
    showAll: useCallback(() => commit(showAllIds(older, max)), [commit, older, max]),
  };
}
